// RevenueCat SDK wrapper for Capacitor.
//
// Web is a graceful no-op — every export resolves to a sensible default so
// pages can call into this lib without platform branching.
//
// Native: lazy-loaded so the iOS/Android runtime cost is only paid where it
// matters. Configure() is called once per app lifetime (after login).
//
// API keys live in import.meta.env.VITE_REVENUECAT_IOS_KEY /
// VITE_REVENUECAT_ANDROID_KEY. Without keys, native init logs a warning
// and degrades to no-op so dev builds keep working.

import * as Sentry from '@sentry/capacitor';
import { isIOS, isAndroid, isNative } from '@/lib/capacitor';

let configuredAppUserId = null;
// The configure call in flight, so boot and a listener asking at once for the
// same user share one Purchases.configure.
let configuring = null;
let purchasesModulePromise = null;

// Last offerings-fetch diagnostic — surfaced in the paywall "Show details"
// disclosure and sent to Sentry, so a blank paywall is debuggable in production
// without a cable or device console.
let lastOfferingsDiagnostic = null;

function platformName() {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'web';
}

export function getOfferingsDiagnostic() {
  return lastOfferingsDiagnostic;
}

// Returns the @revenuecat/purchases-capacitor module namespace (cached).
//
// IMPORTANT: never resolve a promise *to the `Purchases` proxy itself*. The
// Capacitor plugin proxy responds to any property access — including `.then` —
// so a promise resolving to it triggers thenable-assimilation: JS calls
// `Purchases.then(resolve, reject)` over the bridge, which rejects with
// "Purchases.then() is not implemented on ios" AND never resolves, hanging the
// caller (so configure/getOfferings silently never complete). Resolving to the
// (non-thenable) module namespace and destructuring `Purchases` synchronously
// avoids assimilation entirely.
function loadPurchasesModule() {
  if (!purchasesModulePromise) {
    purchasesModulePromise = import('@revenuecat/purchases-capacitor');
  }
  return purchasesModulePromise;
}

function apiKey() {
  if (isIOS()) return import.meta.env.VITE_REVENUECAT_IOS_KEY;
  if (isAndroid()) return import.meta.env.VITE_REVENUECAT_ANDROID_KEY;
  return null;
}

/**
 * Configure RevenueCat with the current user. Idempotent — safe to call
 * multiple times. Does nothing on web or when keys aren't set.
 */
export async function configureRevenueCat(appUserId) {
  if (!isNative()) return;
  if (!appUserId) return;
  if (configuredAppUserId === appUserId) return;
  if (configuring?.appUserId === appUserId) return configuring.promise;

  const promise = configureUser(appUserId).finally(() => {
    if (configuring?.promise === promise) configuring = null;
  });
  configuring = { appUserId, promise };
  return promise;
}

async function configureUser(appUserId) {
  const key = apiKey();
  if (!key) {
    const msg =
      '[RevenueCat] No API key in this build for ' +
      `${platformName()} — VITE_REVENUECAT_*_KEY missing at build time.`;
    console.warn(msg);
    Sentry.captureMessage(msg, {
      level: 'error',
      tags: { area: 'revenuecat.configure' },
      extra: { platform: platformName() },
    });
    return;
  }

  try {
    const { Purchases } = await loadPurchasesModule();
    await Purchases.configure({ apiKey: key, appUserID: appUserId });
    configuredAppUserId = appUserId;
    await linkFirebaseAppInstanceId(Purchases);
  } catch (err) {
    console.error('[RevenueCat] configure failed', err);
    Sentry.captureException(err, {
      tags: { area: 'revenuecat.configure' },
      extra: { platform: platformName() },
    });
  }
}

// The RevenueCat → GA4 server-side integration attributes its events
// (rc_initial_purchase, rc_renewal, …) to the device's GA4 identity via the
// Firebase app instance id — without this link the events arrive in GA4 as
// orphans with no user/session. Device-scoped, so once per configure is
// enough. Failure is non-fatal: purchases still work, only the server-side
// GA4 attribution is lost.
async function linkFirebaseAppInstanceId(Purchases) {
  try {
    const { FirebaseAnalytics } = await import('@capacitor-firebase/analytics');
    const { appInstanceId } = await FirebaseAnalytics.getAppInstanceId();
    if (appInstanceId) {
      await Purchases.setFirebaseAppInstanceID({
        firebaseAppInstanceID: appInstanceId,
      });
    }
  } catch (err) {
    console.warn('[RevenueCat] Firebase app instance id link failed', err);
  }
}

/**
 * Switch the active RevenueCat user. Called after login on the mobile client.
 */
export async function loginRevenueCat(appUserId) {
  if (!isNative() || !appUserId) return;
  if (!configuredAppUserId) {
    await configureRevenueCat(appUserId);
    return;
  }

  try {
    const { Purchases } = await loadPurchasesModule();
    await Purchases.logIn({ appUserID: appUserId });
    configuredAppUserId = appUserId;
  } catch (err) {
    console.error('[RevenueCat] logIn failed', err);
  }
}

// Shared core for both offering lookups: runs the not-native / not-configured
// guards, fetches offerings once, records lastOfferingsDiagnostic, and returns
// the offering that `pick(result)` selects. fetchOfferings picks result.current
// (the dashboard default); fetchOffering picks result.all[id] so a NON-default
// offering can be surfaced without touching the default. Keeping both on one
// path guarantees their guards/diagnostics never drift.
async function resolveOffering(pick, { requestedOfferingId = null } = {}) {
  const platform = platformName();

  if (!isNative()) {
    lastOfferingsDiagnostic = { step: 'not-native', platform };
    return null;
  }
  if (!configuredAppUserId) {
    lastOfferingsDiagnostic = {
      step: 'not-configured',
      platform,
      hasKey: !!apiKey(),
    };
    Sentry.captureMessage('[RevenueCat] offerings: SDK not configured', {
      level: 'warning',
      tags: { area: 'revenuecat.offerings' },
      extra: lastOfferingsDiagnostic,
    });
    return null;
  }

  try {
    const { Purchases } = await loadPurchasesModule();
    const result = await Purchases.getOfferings();
    const offering = pick(result) || null;
    const packageCount = offering?.availablePackages?.length || 0;
    const step = offering
      ? packageCount > 0
        ? 'ok'
        : 'empty'
      : requestedOfferingId
        ? 'offering-not-found'
        : 'empty';
    lastOfferingsDiagnostic = {
      step,
      platform,
      ...(requestedOfferingId ? { requestedOfferingId } : {}),
      currentOfferingId: result?.current?.identifier || null,
      packageCount,
      allOfferingIds: Object.keys(result?.all || {}),
    };
    if (step !== 'ok') {
      // Offering missing, or resolved but StoreKit returned no purchasable
      // products — the usual cause of a blank paywall once config is correct.
      Sentry.captureMessage(
        requestedOfferingId
          ? `[RevenueCat] offering '${requestedOfferingId}' ${
              step === 'offering-not-found'
                ? 'not found'
                : 'empty (no products)'
            }`
          : '[RevenueCat] offerings empty (no products)',
        {
          level: 'warning',
          tags: { area: 'revenuecat.offerings' },
          extra: lastOfferingsDiagnostic,
        },
      );
    }
    return offering;
  } catch (err) {
    lastOfferingsDiagnostic = {
      step: 'error',
      platform,
      ...(requestedOfferingId ? { requestedOfferingId } : {}),
      code: err?.code,
      message: err?.message,
      underlying: err?.underlyingErrorMessage || err?.readableErrorCode,
    };
    console.error('[RevenueCat] getOfferings failed', err);
    Sentry.captureException(err, {
      tags: { area: 'revenuecat.offerings' },
      extra: lastOfferingsDiagnostic,
    });
    return null;
  }
}

/**
 * Fetch the current (dashboard-default) offering from RevenueCat.
 * Returns null on web or when not configured.
 */
export async function fetchOfferings() {
  return resolveOffering((result) => result?.current || null);
}

/**
 * Select a SPECIFIC offering by its dashboard identifier (the result.all key,
 * e.g. 'weekly_v2' — NOT the ofrng… object id). Reads result.all only, never
 * result.current, so a NON-default offering (weekly_v2 stays non-default per the
 * #363 contract) can be surfaced for controlled sandbox verification without
 * flipping the dashboard default. Returns null (recording an
 * 'offering-not-found' diagnostic) rather than silently falling back to current.
 * A blank id falls through to fetchOfferings().
 */
export async function fetchOffering(offeringId) {
  if (!offeringId) return fetchOfferings();
  return resolveOffering((result) => result?.all?.[offeringId] || null, {
    requestedOfferingId: offeringId,
  });
}

/**
 * Trigger a purchase. Returns the full MakePurchaseResult ({ customerInfo,
 * productIdentifier, transaction }) on success, throws on cancellation or
 * failure. The transaction's identifier lets analytics cross-reference the
 * client purchase event against the server-side RevenueCat → GA4 events.
 */
export async function purchasePackage(pkg) {
  if (!isNative()) {
    throw new Error('Purchases are only available on iOS or Android');
  }

  const { Purchases } = await loadPurchasesModule();
  return Purchases.purchasePackage({ aPackage: pkg });
}

/**
 * Purchase an iOS package with a RevenueCat-signed Apple promotional offer.
 * The caller validates that this is Bosko's exact zero-price seven-day offer.
 */
export async function purchaseDiscountedPackage(pkg, promotionalOffer) {
  if (!isIOS()) {
    throw new Error('Discounted packages are only available on iOS');
  }

  const { Purchases } = await loadPurchasesModule();
  return Purchases.purchaseDiscountedPackage({
    aPackage: pkg,
    discount: promotionalOffer,
  });
}

/**
 * Purchase a specific Android SubscriptionOption. Bosko always passes the
 * validated trial-7-days offer explicitly and never relies on the SDK's default
 * resolution, which can select the immediate-paid base plan. iOS exposes no
 * subscriptionOptions and applies eligible intro offers automatically, so iOS
 * uses purchasePackage on the separate physical product instead. Returns the
 * same MakePurchaseResult shape as purchasePackage (#363).
 */
export async function purchaseSubscriptionOption(subscriptionOption) {
  if (!isNative()) {
    throw new Error('Purchases are only available on iOS or Android');
  }

  const { Purchases } = await loadPurchasesModule();
  return Purchases.purchaseSubscriptionOption({ subscriptionOption });
}

/**
 * Per-user free-trial / intro-price eligibility for the given store product ids.
 * iOS StoreKit exposes a product's configured introPrice to EVERYONE regardless
 * of whether this Apple ID is still trial-eligible, so a churned iOS subscriber
 * would otherwise be shown "Start free trial" and then charged in full at the
 * sheet. This is the only API that answers per-user eligibility (#363). Returns
 * a map { [productId]: { status } } (status: 0 unknown, 1 ineligible, 2 eligible,
 * 3 no-intro-offer). Null on web / not-native / error. iOS checkout fails
 * closed unless status 2 proves the intro or status 1 yields a signed free
 * promotional offer; it must never substitute an immediate charge. Android
 * filters ineligible offers out of subscriptionOptions, so this API is only
 * meaningful on iOS.
 */
export async function fetchTrialEligibility(productIdentifiers) {
  if (!isNative()) return null;
  if (!Array.isArray(productIdentifiers) || productIdentifiers.length === 0) {
    return null;
  }

  try {
    const { Purchases } = await loadPurchasesModule();
    return await Purchases.checkTrialOrIntroductoryPriceEligibility({
      productIdentifiers,
    });
  } catch (err) {
    console.error(
      '[RevenueCat] checkTrialOrIntroductoryPriceEligibility failed',
      err,
    );
    return null;
  }
}

/**
 * Ask RevenueCat to sign an Apple promotional offer for the current customer.
 * Null is deliberately fail-closed: callers must not replace it with a normal
 * package purchase because that would charge immediately.
 */
export async function fetchPromotionalOffer(product, discount) {
  if (!isIOS() || !product || !discount) return null;

  try {
    const { Purchases } = await loadPurchasesModule();
    return (await Purchases.getPromotionalOffer({ product, discount })) || null;
  } catch (err) {
    console.error('[RevenueCat] getPromotionalOffer failed', err);
    Sentry.captureException(err, {
      tags: { area: 'revenuecat.promotional_offer' },
      extra: {
        productId: product.identifier,
        offerId: discount.identifier,
      },
    });
    return null;
  }
}

/**
 * Restore previously-purchased entitlements. Required UX on iOS.
 */
export async function restorePurchases() {
  if (!isNative()) return null;

  try {
    const { Purchases } = await loadPurchasesModule();
    const result = await Purchases.restorePurchases();
    return result.customerInfo;
  } catch (err) {
    console.error('[RevenueCat] restorePurchases failed', err);
    throw err;
  }
}

// Active subscribers do not mount the offering/paywall configuration hook.
// Never restore a receipt under an anonymous or previously signed-in identity.
export async function restorePurchasesForUser(appUserId) {
  await configureRevenueCat(appUserId);
  if (!isNative() || !appUserId || configuredAppUserId !== appUserId) {
    throw new Error('RevenueCat is not configured for the authenticated user');
  }
  return restorePurchases();
}

/**
 * Listen for customer-info updates for the given user — RevenueCat reports a
 * transaction the store completes outside a purchase call (an approved Ask to
 * Buy, a settled pending Play payment) this way, and replays the last known
 * info on subscribe. Resolves to a remover, or null on web, for an identity
 * the SDK is not configured with, or when the listener cannot be added.
 */
export async function addCustomerInfoListener(appUserId, listener) {
  await configureRevenueCat(appUserId);
  if (!isNative() || !appUserId || configuredAppUserId !== appUserId) {
    return null;
  }

  try {
    const { Purchases } = await loadPurchasesModule();
    // A Capacitor callback method reports a native error as (null, error).
    const callbackId = await Purchases.addCustomerInfoUpdateListener(
      (customerInfo) => {
        if (customerInfo) listener(customerInfo);
      },
    );
    return () =>
      Purchases.removeCustomerInfoUpdateListener({
        listenerToRemove: callbackId,
      }).catch(() => {});
  } catch (err) {
    console.warn('[RevenueCat] addCustomerInfoUpdateListener failed', err);
    return null;
  }
}

/**
 * Tell the server to reload entitlements after a purchase / restore.
 * Server is the source of truth — RevenueCat webhook should already have
 * landed by the time this runs, but this round-trip closes any race.
 */
export async function reloadServerEntitlements() {
  try {
    const res = await fetch('/api/entitlements', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('[RevenueCat] reloadServerEntitlements failed', err);
    return null;
  }
}
